import React from 'react';
import { Badge } from 'antd';
import { AiOutlineWarning } from 'react-icons/ai';
import { HiOutlineChevronRight } from 'react-icons/hi';
import { useHistory } from 'react-router-dom';
import styled from 'styled-components';
import { DividaTributaria } from '../../operations/dividasTributarias';
import { media } from '../../styles/breakpoints';

interface Props {
  dividas: DividaTributaria[];
}

const AlertCard = styled.div`
  display: flex;
  align-items: center;
  padding: 24px 20px;
  background: #fff;
  border: 1px solid #ffe58f;
  border-left: 3px solid #faad14;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s ease-in-out;
  width: calc(33.333% - 8px);
  min-width: 280px;
  min-height: 100px;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(250, 173, 20, 0.2);
    border-color: #ffc53d;
  }

  &:active {
    transform: translateY(0);
  }

  ${media.md} {
    width: 100%;
    min-width: unset;
    min-height: unset;
    padding: 18px 16px;
    border-radius: 14px;
    border-color: #ffe58f;
    border-left: 3px solid #faad14;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);

    &:hover {
      transform: none;
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
    }

    &:active {
      background: #fffbe6;
      transform: scale(0.98);
    }
  }
`;

const AlertIcon = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 8px;
  background: #fff7e6;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 12px;

  svg {
    width: 20px;
    height: 20px;
    color: #faad14;
  }

  ${media.md} {
    width: 52px;
    height: 52px;
    border-radius: 14px;
    background: linear-gradient(135deg, #fff7e6 0%, #ffe7ba 100%);
    margin-right: 16px;

    svg {
      width: 26px;
      height: 26px;
    }
  }
`;

const AlertContent = styled.div`
  flex: 1;
  min-width: 0;
`;

const AlertLabel = styled.div`
  font-size: 14px;
  font-weight: 500;
  color: rgba(0, 0, 0, 0.85);

  ${media.md} {
    font-size: 16px;
    font-weight: 600;
    color: rgba(0, 0, 0, 0.88);
    letter-spacing: -0.2px;
  }
`;

const AlertDescription = styled.div`
  font-size: 12px;
  color: rgba(0, 0, 0, 0.45);

  ${media.md} {
    font-size: 13px;
    color: rgba(0, 0, 0, 0.5);
    margin-top: 4px;
  }
`;

const AlertBadge = styled.div`
  margin-left: auto;
  margin-right: 8px;

  ${media.md} {
    margin-left: 12px;
    margin-right: 0;

    .ant-badge-count {
      font-size: 13px;
      font-weight: 600;
      min-width: 24px;
      height: 24px;
      line-height: 24px;
      border-radius: 12px;
      box-shadow: 0 2px 4px rgba(250, 173, 20, 0.3);
    }
  }
`;

const AlertArrow = styled.div`
  color: rgba(0, 0, 0, 0.25);

  svg {
    width: 16px;
    height: 16px;
  }

  ${media.md} {
    display: none;
  }
`;

export const DividasAlert: React.FC<Props> = ({ dividas }) => {
  const history = useHistory();

  if (!dividas || dividas.length === 0) return null;

  const total = dividas.reduce((sum, d) => sum + Number(d.value), 0);
  const formatted = new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL'
  }).format(total);

  const label = dividas.length === 1
    ? 'Pendência tributária'
    : 'Pendências tributárias';

  return (
    <AlertCard onClick={() => history.push('/dividas-tributarias')}>
      <AlertIcon>
        <AiOutlineWarning />
      </AlertIcon>
      <AlertContent>
        <AlertLabel>{label}</AlertLabel>
        {total > 0 && <AlertDescription>Valor total: {formatted}</AlertDescription>}
      </AlertContent>
      <AlertBadge>
        <Badge count={dividas.length} style={{ backgroundColor: '#faad14' }} />
      </AlertBadge>
      <AlertArrow>
        <HiOutlineChevronRight />
      </AlertArrow>
    </AlertCard>
  );
};

export default DividasAlert;
